import { z } from 'zod';

/*
 * Phone numbers normalise to E.164 before zod validates them.
 * This runs inside the schema, so the browser and the server normalise identically
 */
export const DEFAULT_COUNTRY_CODE = '+1';
export function normalizePhoneNumber(input: string, countryCode = DEFAULT_COUNTRY_CODE): string {
  // Everything that is not a digit or a plus
  let normalized = input.replace(/[^+\d]/g, '');

  // 00 across most of the world, 011 from North America.
  normalized = normalized.replace(/^011/, '+').replace(/^00/, '+');

  // A single plus is only meaningful at the front
  normalized = normalized.replace(/^\++/, '+');

  if (normalized.startsWith('+')) return normalized;

  // No country code was typed. Assume one only where the digits cannot mean anything
  // else: ten digits is a NANP number, eleven beginning with 1 is the same number with
  // its country code already on the front.
  if (/^\d{10}$/.test(normalized)) return `${countryCode}${normalized}`;
  if (/^1\d{10}$/.test(normalized)) return `+${normalized}`;

  // Anything else is left alone deliberately; a plausible-looking wrong number is worse
  return normalized;
}

/*
 * An optional phone field: normalised first, then validated as E.164.
 * A blank box means "not provided" rather than an empty string
 */
export function optionalPhone(message = 'Enter a phone number, e.g. (306) 555-0123') {
  return z
    .string()
    .trim()
    .pipe(
      z.union([
        // Blankness is judged on what was typed before normalising.
        z.literal('').transform(() => undefined),
        z
          .string()
          .transform((value) => normalizePhoneNumber(value))
          .pipe(z.e164(message)),
      ]),
    )
    .optional();
}
